<template>
  <div class="finance-chart" ref="chartContainer">
    <div v-if="!data.length" class="chart-loading">Loading chart data...</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { createChart, ColorType, AreaSeries } from 'lightweight-charts';

const props = defineProps({
  data: { type: Array, default: () => [] },
  color: { type: String, default: '#b68a2e' },
  priceFormat: { type: String, default: 'price' },
  pricePrecision: { type: Number, default: 2 },
  label: { type: String, default: 'Price' },
});

const chartContainer = ref(null);
let chart = null;
let series = null;
let resizeObserver = null;

const formatPrice = (value) => {
  if (props.priceFormat === 'compact') {
    return value >= 1000 ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${value.toFixed(2)}`;
  }
  if (props.pricePrecision === 0) {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `$${value.toFixed(props.pricePrecision)}`;
};

const buildChart = () => {
  if (!chartContainer.value) return;

  if (chart) {
    chart.remove();
    chart = null;
    series = null;
  }

  const containerWidth = chartContainer.value.clientWidth;
  const containerHeight = chartContainer.value.clientHeight || 320;

  chart = createChart(chartContainer.value, {
    width: containerWidth,
    height: containerHeight,
    layout: {
      background: { type: ColorType.Solid, color: 'transparent' },
      textColor: '#8a918f',
      fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
      fontSize: 11,
    },
    grid: {
      vertLines: { color: 'rgba(205, 196, 183, 0.25)' },
      horzLines: { color: 'rgba(205, 196, 183, 0.25)' },
    },
    crosshair: {
      vertLine: { color: 'rgba(29, 36, 48, 0.2)', width: 1, style: 2, labelBackgroundColor: '#1d2430' },
      horzLine: { color: 'rgba(29, 36, 48, 0.2)', width: 1, style: 2, labelBackgroundColor: '#1d2430' },
    },
    rightPriceScale: {
      borderColor: 'rgba(205, 196, 183, 0.4)',
      scaleMargins: { top: 0.1, bottom: 0.15 },
    },
    timeScale: {
      borderColor: 'rgba(205, 196, 183, 0.4)',
      timeVisible: false,
      fixLeftEdge: true,
      fixRightEdge: true,
    },
    handleScroll: { vertTouchDrag: false },
  });

  series = chart.addSeries(AreaSeries, {
    lineColor: props.color,
    topColor: props.color + '40',
    bottomColor: props.color + '00',
    lineWidth: 2.4,
    priceFormat: {
      type: 'custom',
      formatter: formatPrice,
    },
    lastValueVisible: true,
    priceLineVisible: true,
    priceLineColor: props.color + '60',
  });

  if (props.data.length) {
    series.setData(props.data);
    chart.timeScale().fitContent();
  }
};

watch(() => props.data, (newData) => {
  if (!chart || !series) {
    if (newData.length) {
      buildChart();
    }
    return;
  }
  if (newData.length) {
    series.setData(newData);
    chart.timeScale().fitContent();
  }
}, { deep: true });

watch(() => props.color, () => {
  if (series) {
    series.applyOptions({
      lineColor: props.color,
      topColor: props.color + '40',
      bottomColor: props.color + '00',
      priceLineColor: props.color + '60',
    });
  }
});

onMounted(async () => {
  await nextTick();
  buildChart();

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (chart) {
        const { width, height } = entry.contentRect;
        chart.applyOptions({ width, height: height || 320 });
      }
    }
  });
  if (chartContainer.value) {
    resizeObserver.observe(chartContainer.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  if (chart) {
    chart.remove();
    chart = null;
  }
});
</script>

<style scoped>
.finance-chart {
  width: 100%;
  height: 320px;
  position: relative;
}

.chart-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-muted, #8a918f);
  font-size: 0.9rem;
}

@media (max-width: 640px) {
  .finance-chart {
    height: 260px;
  }
}
</style>
